/*eslint complexity: off*/
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import ReceiptIcon from '@material-ui/icons/Receipt';

import Paper from '~/components/Paper';
import GridItem from '~/components/GridItem';
import InputShow from '~/components/InputShow';
import ModalDelete from '~/components/ModalDelete';
import PaperButtons from '~/components/PaperButtons';
import GridContainer from '~/components/GridContainer';

import Request from '~/providers/Request';
import ProductRecord from '~/records/ProductRecord';
import { AbilityContext } from '~/contexts/Can';
import { historyPropType } from '~/config/routerPropTypes';
import { products, createProduct, editProduct } from '~/config/routes';
import { packageOptions } from '~/config/constants';

import Context from './context';

const attrName = (attr) => ProductRecord.hummanAttributeName(attr);

function ShowProduct({ history }) {
  const { id } = history.location.route.getParams();
  const { records, addRecords } = useContext(Context);
  const [record, setRecord] = useState(new ProductRecord());
  const { t } = useTranslation();
  const [loading, setLoading] = useState(!records || !records[id]);
  const [modal, setModal] = useState(false);
  const ability = useContext(AbilityContext);
  const [packOptions, setPackOptions] = useState([]);

  useEffect(() => {
    if (records && records[id]) {
      setRecord(record.updateAttributes(records[id]));
    }
    setLoading(true);
    fetchPacks();
    Request.get(`/admin/products/${id}`, {})
      .then((response) => {
        setLoading(false);
        addRecords({ data: [response.data] });
        setRecord(record.updateAttributes(response.data));
      })
      .catch((e) => {
        console.error(e);
        setLoading(false);
      });
  }, []);

  const fetchPacks = () => {
    Request.get('/admin/packs', null, { normalizeJSON: false })
      .then((resp) => {
        const items = resp.data
          .filter((e) => e.attributes)
          .map((e) => ({
            id: Number(e.id),
            name: `${e.attributes.name}`,
          }));
        setPackOptions(items);
      })
      .catch((e) => {
        console.error(e);
      });
  };

  async function handleDelete() {
    setLoading(true);
    Request.del(`/admin/products/${id}`)
      .then(() => {
        toast.success(t('delete-toast-success'));
        history.push(products.spec);
      })
      .catch(() => {
        setLoading(false);
        toast.error(t('delete-toast-fail'));
      });
    setModal(false);
  }

  return (
    <Paper
      Icon={ReceiptIcon}
      iconColor="blue"
      title={t('products.defSingular')}
      loading={loading}
      actions={
        <PaperButtons
          id={id}
          setModal={ability.can('delete', 'Products') ? setModal : null}
          backRoute={products}
          newRoute={ability.can('new', 'Products') ? createProduct : null}
          editRoute={ability.can('edit', 'Products') ? editProduct : null}
        />
      }
    >
      <GridContainer>
        <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
          <InputShow label={attrName('name')} value={record.get('name')} />
        </GridItem>
        <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
          <InputShow
            label={attrName('description')}
            value={record.get('description')}
          />
        </GridItem>
        <GridItem xs={12} sm={2} md={2} lg={2} xl={2}>
          <InputShow
            label={attrName('is_sellable')}
            value={record.get('is_sellable')}
            type="boolean"
          />
        </GridItem>
      </GridContainer>
      <GridContainer>
        <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
          <InputShow
            label={attrName('pack_type')}
            value={packageOptions[record.get('pack_type')]}
          />
        </GridItem>
        {record.get('pack_type') === 'pack' && (
          <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
            <InputShow
              label={attrName('pack')}
              value={
                packOptions.find((e) => e.id === record.get('pack_id'))?.name
              }
            />
          </GridItem>
        )}
      </GridContainer>
      <GridContainer>
        <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
          <InputShow label={attrName('ean')} value={record.get('ean')} />
        </GridItem>

        <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
          <InputShow label={attrName('bin')} value={record.get('bin')} />
        </GridItem>

        <GridItem xs={12} sm={6} md={4} lg={3} xl={2}>
          <InputShow
            label={attrName('has_membership_fee')}
            value={record.get('has_membership_fee')}
            type="boolean"
          />
        </GridItem>

        <GridItem xs={12} sm={6} md={4} lg={3} xl={2}>
          <InputShow
            label={attrName('membership_amount')}
            value={record.get('membership_amount')}
          />
        </GridItem>
      </GridContainer>

      <GridContainer>
        <GridItem xs={12} sm={3} md={3} lg={3} xl={3}>
          <InputShow label={attrName('amount')} value={record.get('amount')} />
        </GridItem>

        <GridItem xs={12} sm={3} md={3} lg={3} xl={3}>
          <InputShow
            label={attrName('currency')}
            value={record.get('currency')}
          />
        </GridItem>

        <GridItem xs={12} sm={2} md={2} lg={2} xl={2}>
          <InputShow
            label={attrName('quantity')}
            value={record.get('quantity')}
          />
        </GridItem>

        <GridItem xs={12} sm={2} md={2} lg={2} xl={2}>
          <InputShow
            label={attrName('unlimited')}
            value={record.get('unlimited')}
            type="boolean"
          />
        </GridItem>

        <GridItem xs={12} sm={2} md={2} lg={2} xl={2}>
          <InputShow
            label={attrName('duration')}
            value={record.get('duration')}
          />
        </GridItem>
      </GridContainer>

      <ModalDelete
        modal={modal}
        onClose={() => {
          setModal(false);
        }}
        name={record.get('name')}
        id={record.get('id')}
        deleteAction={handleDelete}
      />
    </Paper>
  );
}

ShowProduct.propTypes = {
  history: historyPropType,
};

export default ShowProduct;
